// SSO Utility Functions

export interface SSOConfig {
  id?: number
  provider: string
  enabled?: boolean
  config: any
  organizationId: number
}

export interface TestConnectionResult {
  success: boolean
  message: string
}

// Get provider display name for UI
export const getProviderDisplayName = (provider: string): string => {
  switch (provider.toLowerCase()) {
    case 'oidc': return 'Custom OIDC'
    case 'google': return 'Google Workspace'
    case 'microsoft': return 'Microsoft Entra ID'
    default: return provider
  }
}

// Get provider config ID from useFieldArray
export const getProviderConfigId = (ssoConfigs: SSOConfig[], provider: string): number | undefined => {
  const config = ssoConfigs.find(config => config.provider === provider)
  return config?.id
}

// Check if any SSO is enabled for organization
export const hasEnabledSSO = (ssoConfigs: SSOConfig[]): boolean => {
  return ssoConfigs.some(config => config.enabled === true)
}

// Get enabled SSO configs only
export const getEnabledSSOConfigs = (ssoConfigs: SSOConfig[]): SSOConfig[] => {
  return ssoConfigs.filter(config => config.enabled === true)
}

// Generate SSO state for security
export const generateSSOState = (organizationId: number, autoCreate: boolean): string => {
  return `react-${Date.now()}-org-${organizationId}${autoCreate ? '-autocreate' : ''}`
}

// Store SSO callback data in localStorage
export const storeSSOCallbackData = (
  state: string, 
  organizationId: number, 
  provider: string, 
  autoCreate: boolean
): void => {
  localStorage.setItem('sso-state', state)
  localStorage.setItem('sso-org-id', organizationId.toString())
  localStorage.setItem('sso-provider', provider)
  
  if (autoCreate) {
    localStorage.setItem('sso-auto-create', 'true')
  } else {
    localStorage.removeItem('sso-auto-create')
  }
}

// Clear SSO callback data from localStorage
export const clearSSOCallbackData = (): void => {
  localStorage.removeItem('sso-state')
  localStorage.removeItem('sso-org-id')
  localStorage.removeItem('sso-provider')
  localStorage.removeItem('sso-auto-create')
}

// Get SSO callback data from localStorage
export const getSSOCallbackData = () => {
  return {
    state: localStorage.getItem('sso-state'),
    organizationId: localStorage.getItem('sso-org-id'),
    provider: localStorage.getItem('sso-provider'),
    autoCreate: localStorage.getItem('sso-auto-create') === 'true'
  }
}